import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import type { SentPitch } from '../../outreachTypes';
import { detailsMessages as messages } from './detailsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the details panel */
import styles from './Details.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PitchMailCardProps {
  pitch: SentPitch;
  /** The person's name for the To line. */
  toName: string | null;
  /** Fold the text to 5 lines (a pitch with news: the news comes first). */
  folded: boolean;
}

/**
 * The pitch as the person got it (design outreach-v2 t2-t4): From and To, the subject and the
 * text. A planned pitch shows "Written by Plug" and no address.
 */
const PitchMailCardComponent: React.FC<PitchMailCardProps> = ({ pitch, toName, folded }) => {
  const intl = useIntl();
  const [whole, setWhole] = useState(false);
  const clipped = folded && !whole;
  const written = pitch.subject !== '' || pitch.body !== '';
  const to = [toName, pitch.toEmail === null ? null : `<${pitch.toEmail}>`].filter((part) => part !== null).join(' ');
  return (
    <section className={styles.section} data-testid="pitch-mail">
      <h3 className={styles.sectionHead}>
        {intl.formatMessage(messages.pitchTitle)}
        <span className={styles.sectionNote}>
          {intl.formatMessage(pitch.planned ? messages.pitchWrittenByPlug : messages.pitchWhatTheyGot)}
        </span>
      </h3>
      <div className={styles.mail}>
        {(pitch.fromEmail !== null || to !== '') && (
          <div className={styles.mailHead}>
            {pitch.fromEmail !== null && (
              <>
                <span className={styles.mailKey}>{intl.formatMessage(messages.pitchFrom)}</span>
                <span className={styles.mailValue}>{pitch.fromEmail}</span>
              </>
            )}
            {to !== '' && (
              <>
                <span className={styles.mailKey}>{intl.formatMessage(messages.pitchTo)}</span>
                <span className={styles.mailValue}>{to}</span>
              </>
            )}
          </div>
        )}
        {written ? (
          <>
            <div className={styles.mailSubject}>{pitch.subject}</div>
            <p className={clsx(styles.mailBody, clipped && styles.mailBodyClipped)} data-testid="sent-pitch-body">
              {pitch.body}
            </p>
            {clipped && (
              <div className={styles.mailMore}>
                <SMLButton size="sm" color="link" omitCap className="p-0" onClick={() => setWhole(true)}>
                  {intl.formatMessage(messages.showWhole)}
                </SMLButton>
              </div>
            )}
          </>
        ) : (
          <p className={styles.mailBody}>{intl.formatMessage(messages.pitchNotWritten)}</p>
        )}
      </div>
    </section>
  );
};

export const PitchMailCard = TypedMemo(PitchMailCardComponent);
